import { HttpClientModule } from '@angular/common/http';
import { InjectionToken, Injector, ModuleWithProviders, NgModule } from '@angular/core';
import { ApolloLink, FetchResult, HttpLink, InMemoryCache, Observable, Operation, ServerError, ServerParseError, split } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { createPersistedQueryLink, PersistedQueryLink } from '@apollo/client/link/persisted-queries';
import { RetryLink } from '@apollo/client/link/retry';
import { getMainDefinition, getOperationDefinition } from '@apollo/client/utilities';
import { Apollo, APOLLO_OPTIONS } from 'apollo-angular';
import { sha256 } from 'crypto-hash';

import { GraphQLFormattedError, print } from 'graphql';
import { Client, createClient } from 'graphql-ws';
import { BehaviorSubject } from 'rxjs';

import { environment } from '../../../environments/environment';

import { installBlueSageMockRailConsole } from '../../bluesage/testing/bluesage-mock-rail.console';
import { createBlueSageMockRailLink } from '../../bluesage/testing/bluesage-mock-rail.link';
import { generateUUID } from '../../utilities/state.util';

import { PlusAuthenticationService } from './plus-auth.service';

export interface PlusConnectionOptions {
  graphql: string;
  ws: string;
  websocketsEnabled: boolean;
}

export const PLUS_CONNECTION_OPTIONS = new InjectionToken<PlusConnectionOptions>('plus.connection-options');

export function createApollo(
  injector: Injector,
  plusConnectionOptions: PlusConnectionOptions,
  graphqlModule: GraphQLModule,
) {

  // Dev-only BlueSage test harness. Off unless a scenario is set in
  // localStorage, and unreachable in a production build. FIRST in the chain so
  // a mocked answer never touches auth, persisted queries or the retry link —
  // it is standing in for the far side of the network, not for the backend.
  installBlueSageMockRailConsole(injector);

  const webLink = ApolloLink.from([

    createBlueSageMockRailLink(),

    // createRetryCountTrackerLink(),

    createPersistedQueryLinkWithOptions(),

    // add nonce header to uri
    createNonceLink(),

    // // retry if status code is 0 or 502
    createRetryLink(),


    // // add auth headers and call auth if necessary
    createAuthLink(injector, graphqlModule),

    // // handle errors and let user know of no connections
    createHandleErrorLink(injector),

    // httpLink.create({
    //   uri: '/api/graphql',
    //   withCredentials: true,
    //   includeExtensions: true,
    // }),
    // add zone and role headers
    addContextHeaders(
      graphqlModule, plusConnectionOptions,
    ),

    new HttpLink({
      // uri: (operation) => operation.getContext().uri, // Use the updated URI

      // withCredentials: true,
      includeExtensions: true,
      // includeQuery: false,
    }),

    // httpLink.create({
    // }),

  ]);

  let link: ApolloLink;

  // if we support websockets then add websocket link
  const supportsWebSockets = 'WebSocket' in window || 'MozWebSocket' in window;
  if (plusConnectionOptions.ws && supportsWebSockets && plusConnectionOptions.websocketsEnabled) {

    const wsLink = createWebsocketLink(graphqlModule);

    link = split(
      ({ query }) => {
        const { kind, operation }: any = getMainDefinition(query);
        return kind === 'OperationDefinition' && operation === 'subscription';
      },
      wsLink,
      webLink,
    );
  }

  return {
    link,
    cache: makeCache(),
  };
}

export function createNonceLink() {
  return setContext((operation, previousContext) => {

    const operationDefinition = getOperationDefinition(operation.query);

    // only set the nonce for mutations
    if (operationDefinition?.operation !== 'mutation') {
      return {};
    }

    // add a nonce header to all queries
    // mutations will error if they are

    const nonce = generateUUID();
    return {
      headers: {
        ...previousContext.headers,
        'x-nonce': nonce,
      },
    };
  });
}

export const maxRetryAttempts = 3;
export function createRetryLink() {

  const retryLink = new RetryLink({
    delay: {
      initial: 300,           // Initial delay in milliseconds
      max: 1500,              // Maximum delay in milliseconds
      jitter: true,           // Apply random jitter to delay
    },
    attempts: {
      max: maxRetryAttempts,                 // Maximum number of retry attempts
      // retry if status code is 0 (no connection) or 502 (bad gateway)
      retryIf: (error, _operation) => {
        // Check if the error has a response with status code 0 or 502
        const statusCode = error?.statusCode;

        // Requirement: Retry parse/truncation errors only for idempotent operations (queries + authenticate mutation).
        const def = getMainDefinition(_operation.query);
        const isQuery = def.kind === 'OperationDefinition' && def.operation === 'query';
        const isRetryableParseOp = isQuery || _operation.operationName === 'authenticate';

        // Requirement: a MUTATION is never re-sent — `authenticate` excepted, as
        // it always was for parse errors. Every other mutation carries an
        // `x-nonce` that the gateway burns the moment the original request is
        // read, so a retry of the same operation can only ever answer
        // `400 Nonce already used for mutation` — which is exactly what
        // happened on production on 2026-09-03: the ingress cut a long terminal
        // push at 60 s with a 504, this link re-sent it, and the operator saw a
        // misleading 400 while the device carried on. The retry never helped a
        // mutation; it only replaced the real error with a wrong one.
        const doRetry = isRetryableParseOp
            && (isNotConnectedError(error) || isParseError(error));

        // Get the current retry count from context
        const currentRetryCount = _operation.getContext()?.retryCount ?? 0;

        if (doRetry) {
          console.warn(`[GQL] Retrying ${ _operation.operationName } (${ statusCode })`);

          // Increment and update the retry count in context
          _operation.setContext({
            retryCount: currentRetryCount + 1,
            headers: {
              ..._operation.getContext()?.headers,
              'x-attempt': currentRetryCount + 1,
            },
          });
        }

        return doRetry;
      },
    },
  });

  return retryLink;
}

export function createAuthLink(
  injector: Injector,
  graphqlModule: GraphQLModule,
) {

  const authLink = new ApolloLink((op, forward) => {
    let promiseResolve: () => void;
    const prom = new Promise<void>((resolve, _reject) => {
      promiseResolve = resolve;
    });
    function resolveInFlightPromise() {
      if (promiseResolve) {
        // resolve at the end of the event queue
        setTimeout(() => {
          promiseResolve();
          const index = graphqlModule.inFlightPromises.indexOf(prom);
          if (index >= 0) {
            graphqlModule.inFlightPromises.splice(index, 1);
          }
        }, 1);
      }
    }

    const authService = injector.get(PlusAuthenticationService);

    const isAuthOp = [
      'authenticate',
      'deauthenticate',
    ].includes(op?.operationName);

    const authPromise = authService.checkAuthStatus(!isAuthOp, !isAuthOp);

    graphqlModule.inFlightPromises.push(prom);

    return graphqlModule.waitForReady()
      // wait for the auth observable to resolve before we proceed with the query
      .flatMap(() => new Observable((observer) => {
        authPromise.then((res) => {
          if (!isAuthOp && res === 'deauthenticating'){
            observer.error(res);
            return;
          }

          observer.next(res);
          observer.complete();
        });

        return () => {};
      }))
      .flatMap(
        () => forward(op).map((response) => {
          if (response && authService.connectionStatus.value !== 'connected') {
            authService.connectionStatus.next('connected');
          }

          if (isAuthOp) {
            resolveInFlightPromise();
            return response;
          }

          // Do not set zone if we are in the middle of a zone change
          if (graphqlModule.zoneLoadingCount > 0) {
            resolveInFlightPromise();
            return response;
          }

          const context = op.getContext();
          const {
            response: { headers }
          } = context;

          // Use the zone/role that was stamped onto the request at send time
          // (by addQueryParams). This avoids a race where a stale response
          // from Zone A arrives after we have already switched to Zone B and
          // incorrectly overwrites graphqlModule.zone back to Zone A.
          const desiredZone = context.requestZone ?? graphqlModule.zone.value;
          const desiredRole = context.requestRole ?? graphqlModule.role.value;

          // get user from header
          if (headers) {

            // TODO: initial login has zone set incorrectly for branding query
            const resolvedZone = headers.get('x-zone') || undefined;
            const resolvedRole = headers.get('x-role') || undefined;

            // Only update global zone/role state when this response belongs to
            // the currently active context AND the backend resolved a different
            // value (e.g. it normalised a child zone to its parent). Responses
            // from a previous context (requestZone/Role !== current) are stale
            // and must never overwrite the current zone or role.
            const isCurrentZoneRequest = desiredZone === graphqlModule.zone.value;
            // Allow role updates during bootstrap (!graphqlModule.role.value) but
            // otherwise require the request role to match the current role so a
            // stale same-zone response with an old role cannot flip it back.
            const isCurrentRoleRequest =
              !graphqlModule.role.value || desiredRole === graphqlModule.role.value;
            const backendResolvedDifferentZone = isCurrentZoneRequest && desiredZone !== resolvedZone;
            const backendResolvedDifferentRole =
              isCurrentZoneRequest && isCurrentRoleRequest && desiredRole !== resolvedRole;

            if ((resolvedZone && !graphqlModule.zone.value) || (resolvedZone && backendResolvedDifferentZone)) {
              graphqlModule.zone.next(resolvedZone);
              persistZone(resolvedZone);
            }
            if ((resolvedRole && !graphqlModule.role.value) || (resolvedRole && backendResolvedDifferentRole)) {
              graphqlModule.role.next(resolvedRole);
              persistRole(resolvedRole);
            }

            const user = headers.get('x-user') || undefined;
            const deauthenticated = headers.get('x-deauthenticated');

            // Skip deauthenticated redirect on auth pages (login, logged-out,
            // reset-password, signup) — the authenticate({}) bootstrap call
            // naturally returns x-deauthenticated when there are no cookies,
            // and we don't want to trigger a login loop. /login is the legacy
            // alias for /auth/login (immediate redirectTo) — its bootstrap can
            // run before the redirect, so it must be excluded here too or
            // deauth cleanup would wipe pre_login_url before the login page
            // can restore/record it.
            const isAuthPage = typeof window !== 'undefined'
              && (window.location.pathname.startsWith('/auth/')
                || window.location.pathname === '/login');

            if (deauthenticated && op.operationName !== 'deauthenticate' && !isAuthPage) {
              // No location.reload() here: it raced the goToLogin navigation
              // below, and when the reload won, the app re-booted on the same
              // protected page — where the bootstrap request x-deauthenticated
              // again, re-entering this handler and looping (users observed
              // dozens of /auth/login views per session). onDeauthenticated
              // already cleans up (storage, apollo store, websocket) and
              // navigates to the login page, which either restores the
              // cookie session in place or redirects straight to FusionAuth.
              authService.onDeauthenticated(true);
            } else if (!user && authService.user && !isAuthPage) {
              authService.notifySessionExpired();
              authService.onDeauthenticated(true);
            }
          }

          // setTimeout(() => {
          resolveInFlightPromise();
          // }, 250);
          return response;

        })
      );
    // .map
  });

  return authLink;
}

export function createPersistedQueryLinkWithOptions() {

  if (!environment.production) {
    // noop
    return setContext((_operation, _previousContext) => ({}));
  }

  const options: PersistedQueryLink.Options = {
    // retry: (err) => {
    //   console.error(`PQ RETRY`, err);
    //   return false;
    // },

    // disable: true,

    sha256: sha256,
    useGETForHashedQueries: false,
    // generateHash,

  };

  return createPersistedQueryLink(options);

}
export function addContextHeaders(
  graphqlModule: GraphQLModule,
  plusConnectionOptions: PlusConnectionOptions,
) {
  return setContext((operation, previousContext) => {
    let zone = graphqlModule.zone.value;
    let role = graphqlModule.role.value;

    previousContext.zone = resolveZoneFromContext(previousContext);
    if (previousContext.zone) {
      zone = previousContext.zone;
      role = previousContext.role;
    }

    // Build the URI with just the operation name query param (for logging).
    const queryParams = new URLSearchParams({
      n: operation.operationName.slice(0, 32),
    });
    const uriWithParams = `${plusConnectionOptions.graphql}?${queryParams.toString()}`;

    // Send zone as a header on every request. The backend resolves the
    // user's roles from the zone cache (additive across all roles in the
    // zone's ancestry) — no x-role needed.
    const headers: Record<string, string> = {
      ...previousContext.headers,
    };
    if (zone) {
      headers['x-zone'] = zone;
    }

    return {
      ...previousContext,
      uri: uriWithParams,
      headers,
      // Stamp the zone/role used at request time so the response handler can
      // compare against them rather than the current (possibly-changed) zone.
      requestZone: zone,
      requestRole: role,
    };
  });
}

export function createHandleErrorLink(injector: Injector) {
  return onError(({
    graphQLErrors,
    networkError,
    operation,
    forward: _forward,
  }) => {
    const retryCount = operation.getContext()?.retryCount;
    const authService = injector.get(PlusAuthenticationService);

    const [gqlError] = graphQLErrors || [];

    if (isServerError(networkError) && isAuthenticationError(gqlError)) {
      handleAuthenticationError(authService);
    } else if (isServerError(networkError) && gqlError) {
      handleGraphQLError(graphQLErrors, networkError, operation);
    } else if (isServerError(networkError) || isNotConnectedError(networkError)) {
      handleServerError(networkError, retryCount || 0, authService);
    } else if (isParseError(networkError)) {
      // Transient truncated/empty response, almost always iOS/WebKit cutting the
      // connection mid-body. Query retries (above) are already exhausted by now,
      // so log as a warning rather than dumping it into "Unknown network error".
      console.warn(`[GQL] Response parse error on ${operation.operationName} (likely truncated response)`, networkError);
    } else if (networkError) {
      console.error('[GQL] Network error:', networkError?.message || networkError?.name || networkError);
    } else if (graphQLErrors?.length > 1) {
      const messages = graphQLErrors.map((e: any) => e?.message || String(e));
      console.error(`[GQL] ${graphQLErrors.length} errors on ${operation.operationName}:`, messages);
    } else if (gqlError && gqlError.extensions?.code !== 'PERSISTED_QUERY_NOT_FOUND') {
      // Permission-denied errors on getConfigValues degrade gracefully (empty
      // config) — suppress the console.error to avoid noise in DD RUM for the
      // ~192 errors/24h from roles that lack config.view.
      const isConfigPermissionDenied = operation.operationName === 'getConfigValues'
        && gqlError?.extensions?.code === 'FORBIDDEN'
        && (gqlError?.message || '').includes('Missing permission config.view');
      if (isConfigPermissionDenied) {
        console.warn(`[GQL] ${operation.operationName}: config.view denied (graceful degradation)`);
      } else {
        console.error(`[GQL] ${operation.operationName}:`, gqlError?.message || gqlError);
      }
    }
  });
}

// Type guard to check if the error is an instance of HttpErrorResponse
function isServerError(error: any): error is ServerError {
  return error?.name === 'ServerError' && error?.statusCode;
}

// Type guard for a ServerParseError: the HTTP body could not be JSON-parsed.
// An empty or truncated (non-HTML) body is the signature of a connection cut
// mid-response. WebKit/iOS surfaces these as "JSON Parse error: Unexpected EOF"
// instead of a network error, so they slip past isNotConnectedError. They are
// transient, so we treat them as retryable. An HTML body, by contrast, is a
// gateway/proxy error page - a different problem we don't retry here.
function isParseError(error: any): error is ServerParseError {
  if (error?.name !== 'ServerParseError') { return false; }
  const body = (error.bodyText ?? '').trim();
  return body === '' || !body.startsWith('<');
}

// Helper function to check if the error is an authentication error
function isAuthenticationError(gqlError: GraphQLFormattedError): boolean {
  return gqlError?.extensions?.code === 'UNAUTHENTICATED';
}

// Handle authentication errors by deauthenticating. No location.reload() here:
// like the x-deauthenticated response-header branch above, a reload races the
// goToLogin navigation and, when the reload wins, re-boots the app on the same
// protected page — where the request fails again and re-enters this handler.
// onDeauthenticated already cleans up (storage, apollo store, websocket) and
// navigates to the login page itself.
function handleAuthenticationError(authService: PlusAuthenticationService) {
  if (authService.jwtPayload) {
    authService.onDeauthenticated(true);
  }
}

// Handle GraphQL errors by logging them and optionally breaking on errors
function handleGraphQLError(errors: readonly GraphQLFormattedError[], serverError: ServerError, operation: Operation) {
  if (environment.breakAtGraphqlErrors) {
    debugger;
  }
  console.error(`[GQL] ${operation.operationName}:`, errors.length > 1 ? errors : errors[0]);
}

// Handle network errors by checking retry count and connection status
function handleServerError(
  error: ServerError | Error,
  retryCount: number,
  authService: PlusAuthenticationService
) {
  // const isClientError = false;

  // if (networkError.error?.errors) {
  //   for (const err of networkError.error.errors) {
  //     if (err.extensions.code === 'BAD_USER_INPUT') {
  //       console.error('[Error]: Bad Input Supplied to Function');
  //       isClientError = true;
  //     } else if (err.extensions.code === 'GRAPHQL_VALIDATION_FAILED') {
  //       console.error('[Error]: GraphQL Schema is Invalid');
  //       isClientError = true;
  //     } else {
  //       console.error(networkError);
  //     }
  //   }
  // }

  if (isNotConnectedError(error) && retryCount >= maxRetryAttempts - 1) {
    console.error('[GQL] Connection lost:', error);
    authService.connectionStatus.next('disconnected');
    authService.pollConnection();
  }
}

function isNotConnectedError(error: Error | ServerError) {
  if (!error) { return false; }

  const status = 'statusCode' in error && error?.statusCode;
  if ((status >= 502 && status <= 504) || status === 0) {
    return true;
  }

  if (error?.message?.includes('Load failed')) {
    return true;
  }

  return false;
}

export function createWebsocketLink(
  graphqlModule: GraphQLModule,
) {

  const wsProtocol = location.protocol === 'http:' ? 'ws:' : 'wss:';

  const wsUrl = `${ wsProtocol }//${ location.host }/api/subscriptions`;

  if (!graphqlModule.wsClient) {
      graphqlModule.wsClient = createClient({
        url: wsUrl,
        retryAttempts: Infinity,
        shouldRetry: () => true,
        keepAlive: 10000,


        // eslint-disable-next-line arrow-body-style
        connectionParams: () => {
          // TODO: reconnect on zone change

          return {
            zone: graphqlModule.zone.value,
            role: graphqlModule.role.value,
          };
        },

        on: {
          closed: () => {
            graphqlModule.connectionStatus.next('disconnected');

          },
          connected: () => {
            graphqlModule.connectionStatus.next('connected');
          },
          error: (err) => {
            console.error('[GQL] WebSocket error:', err);
          },
        },
      });
    }

    const wsLink = new GraphQLWsLink(graphqlModule.wsClient);
    return wsLink;
}

export function makeCache() {
  return new InMemoryCache({
    resultCaching: true,
    // fragmentMatcher: new IntrospectionFragmentMatcher({ introspectionQueryResultData }),
    typePolicies: {
      Query: {
        fields: {
          jobs: {
            // Merge the existing jobs array if incoming does not have it
            merge(existing, incoming) {
              return {
                ...existing,
                ...incoming,
                jobs: incoming.jobs ? incoming.jobs : existing.jobs,
              };
            },
          },
          users: {
            // Merge the existing users array if incoming does not have it
            merge(existing, incoming) {
              return {
                ...existing,
                ...incoming,
                users: incoming.users ? incoming.users : existing.users,
              };
            },

          }
        },
      },
    }
  });
}

function resolveZoneFromContext(opContext: any): string | undefined {
  if (!opContext) { return undefined; }

  if (opContext.zone) {
    return opContext.zone;
  }

  if (opContext['x-zone']) {
    return opContext['x-zone'];
  }

  if (opContext.headers && opContext.headers['x-zone']) {
    return opContext.headers['x-zone'];
  }

  return undefined;
}

// ─── Zone/role sessionStorage + localStorage persistence ──────────────────
// sessionStorage is per-tab: each tab tracks its own zone/role.
// localStorage is the fallback for new tabs and page reloads.

const SS_ZONE_KEY = 'mr_zone';
const SS_ROLE_KEY = 'mr_role';
const LS_ZONE_KEY = 'mr_zone';
const LS_ROLE_KEY = 'mr_role';

function persistZone(zone: string): void {
  try {
    sessionStorage.setItem(SS_ZONE_KEY, zone);
    localStorage.setItem(LS_ZONE_KEY, zone);
  } catch { /* storage may be unavailable */ }
}

function persistRole(role: string): void {
  try {
    sessionStorage.setItem(SS_ROLE_KEY, role);
    localStorage.setItem(LS_ROLE_KEY, role);
  } catch { /* storage may be unavailable */ }
}

function hydrateZoneFromStorage(): string | undefined {
  try {
    return sessionStorage.getItem(SS_ZONE_KEY) || localStorage.getItem(LS_ZONE_KEY) || undefined;
  } catch { return undefined; }
}

function hydrateRoleFromStorage(): string | undefined {
  try {
    return sessionStorage.getItem(SS_ROLE_KEY) || localStorage.getItem(LS_ROLE_KEY) || undefined;
  } catch { return undefined; }
}



export class PlusApollo extends Apollo { }

export class GraphQLWsLink extends ApolloLink {
  constructor(private client: Client) {
    super();
  }

  public request(operation: Operation): Observable<FetchResult> {
    // eslint-disable-next-line arrow-body-style
    return new Observable((sink) => {
      return this.client.subscribe<FetchResult>(
        { ...operation, query: print(operation.query) },
        {
          next: sink.next.bind(sink),
          complete: sink.complete.bind(sink),
          error: sink.error.bind(sink),
        },
      );
    });
  }
}

export type WSConnectionStatus = 'connected' | 'disconnected';

// @dynamic
@NgModule({
  imports: [
    // ApolloModule,
    HttpClientModule,
    // HttpLinkModule
  ],
  exports: [
    // ApolloModule,
    HttpClientModule,
    // HttpLinkModule
  ],
  providers: [],
})
export class GraphQLModule {

  wsClient: Client;
  inFlightPromises: Promise<void>[] = [];

  readyResolve: () => void;
  readyWaiting: Promise<void>;

  /**
   * A behaviour subject of the currently contexted zone
   */
  public zone = new BehaviorSubject<string>(undefined);

  /**
   * A behaviour subject of the currently contexted zone
   */
  public role = new BehaviorSubject<string>(undefined);

  public connectionStatus = new BehaviorSubject<WSConnectionStatus>('disconnected');

  /**
   * String combination of zone:role used to context
   * into the backend
   */
  public get context() {
    return `${this.zone}:${this.role}`;
  }

  /**
   * Ref-count of active setContext() calls in flight.
   * The auth-link response handler suppresses zone/role overwrite from stale
   * responses while this is > 0. Using a counter instead of a boolean means
   * overlapping setContext() calls don't clear the guard prematurely.
   */
  public zoneLoadingCount = 0;

  /** @deprecated use zoneLoadingCount; kept for template backward-compat */
  public get newZoneLoading() { return this.zoneLoadingCount > 0; }

  constructor() {
    // Hydrate zone/role from sessionStorage (per-tab) or localStorage (fallback).
    // This ensures the correct zone is sent on the very first GraphQL request
    // after a page reload, before the backend has a chance to respond with
    // x-zone/x-role headers.
    const storedZone = hydrateZoneFromStorage();
    const storedRole = hydrateRoleFromStorage();
    if (storedZone) { this.zone.next(storedZone); }
    if (storedRole) { this.role.next(storedRole); }
  }

  public static forRoot(config?: PlusConnectionOptions): ModuleWithProviders<GraphQLModule> {
    return {
      ngModule: GraphQLModule,
      providers: [
        {
          provide: PLUS_CONNECTION_OPTIONS,
          useValue: config || {
            graphql: 'localhost:1337/api/graphql',
            ws: 'localhost:1337/api/subscriptions',
          }
        },
        {
          provide: APOLLO_OPTIONS,
          useFactory: createApollo,
          deps: [
            Injector,
            PLUS_CONNECTION_OPTIONS,
            GraphQLModule,
          ],
        },
        {
          provide: PlusApollo,
          useExisting: Apollo,
        }
      ]
    };
  }

  /**
  * @deprecated Use `reconnectWebsocket()` instead.
  * Re-initialize the websocket connection.
  */
  reinitWebsocket() {
    if (!this.wsClient) { return; }
    // re-initialize the websocket connection
    // this.wsClient.close(false, false);
    // console.log(`Reinitializing the websocket connection`);
  }

  // Re-connect the websocket connection
  reconnectWebsocket() {
    if (!this.wsClient) { return; }
    this.wsClient?.terminate();
  }



  markWaiting() {
    this.markReady();

    this.readyWaiting = new Promise((resolve) => {
      this.readyResolve = resolve;
    });
  }

  async waitForInflightQueries(timeout = 5000) {
    return new Promise<void>(async (resolve) => {
      const timeoutHandle = setTimeout(() => {
        this.inFlightPromises = [];
        resolve();
      }, timeout);
      Promise.all(this.inFlightPromises)
        .catch()
        .then((_vals) => {
          this.inFlightPromises = [];
          clearTimeout(timeoutHandle);
          resolve();
        });
    });

  }

  markReady() {
    if (this.readyResolve) {
      this.readyResolve();
      this.readyResolve = undefined;
      this.readyWaiting = undefined;
    }
  }

  waitForReady() {
    return new Observable<boolean>((observer) => {
      if (!this.readyWaiting) {
        observer.next(true);
        observer.complete();
        return;
      } else {
        this.readyWaiting.then(() => {
          observer.next(true);
          observer.complete();
        });
      }

    });

  }
}
